import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, IconButton, Stack, Typography, useTheme,
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import { alpha } from '@mui/material/styles';
import { CompleteTaskButton } from './assets';

export const runningTaskTiming = (task, now = Date.now()) => {
  const duration = Math.max(0, Number(task?.duration) || 0);
  const savedWorkingTime = Math.max(0, Number(task?.working_time) || 0);
  const startTime = task?.start ? new Date(task.start).getTime() : Number.NaN;
  const currentSession = Number.isFinite(startTime)
    ? Math.max(0, Math.floor((now - startTime) / 1000))
    : 0;
  const workingTime = savedWorkingTime + currentSession;
  const remaining = duration - workingTime;
  const percentRemaining = duration > 0
    ? Math.max(0, Math.min(100, (remaining / duration) * 100))
    : 0;
  return {
    expired: remaining <= 0,
    percentRemaining,
    remaining,
    workingTime: Math.floor(workingTime),
  };
};

const CountdownRing = ({ expired, percentRemaining, title }) => {
  const theme = useTheme();
  const color = expired ? theme.palette.orange.main : theme.palette.primary.main;
  return (
    <Box
      role="img"
      aria-label={expired ? `${title} timer has ended` : `${title} timer countdown`}
      sx={{
        width: { xs: 22, sm: 27 },
        height: { xs: 22, sm: 27 },
        flexShrink: 0,
        borderRadius: '50%',
        background: `conic-gradient(${color} ${percentRemaining}%, ${theme.palette.action.disabledBackground} ${percentRemaining}% 100%)`,
        boxShadow: `0 0 8px ${alpha(color, 0.45)}`,
        display: 'grid',
        placeItems: 'center',
        '&::after': {
          content: '""',
          width: { xs: 14, sm: 17 },
          height: { xs: 14, sm: 17 },
          borderRadius: '50%',
          bgcolor: 'background.default',
        },
      }}
    />
  );
};

const ScrollingTitle = ({ title }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  const measureOverflow = useCallback(() => {
    if (!containerRef.current || !textRef.current) return;
    setOverflowing(textRef.current.scrollWidth > containerRef.current.clientWidth);
  }, []);

  useEffect(() => {
    measureOverflow();
    window.addEventListener('resize', measureOverflow);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(measureOverflow);
    if (resizeObserver && containerRef.current) resizeObserver.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', measureOverflow);
      resizeObserver?.disconnect();
    };
  }, [measureOverflow, title]);

  return (
    <Typography
      ref={containerRef}
      variant="caption"
      component="div"
      aria-label={title}
      sx={{ minWidth: 0, flex: 1, overflow: 'hidden', color: 'primary.main', whiteSpace: 'nowrap' }}
    >
      <Box
        ref={textRef}
        component="span"
        sx={overflowing ? {
          display: 'inline-flex',
          width: 'max-content',
          animation: 'runningTaskTitleScroll 8s linear infinite',
          '@keyframes runningTaskTitleScroll': {
            '0%, 15%': { transform: 'translateX(0)' },
            '85%, 100%': { transform: 'translateX(calc(-50% - 16px))' },
          },
        } : { display: 'inline-block' }}
      >
        <Box component="span">{title}</Box>
        {overflowing && <Box component="span" aria-hidden="true" sx={{ ml: 4 }}>{title}</Box>}
      </Box>
    </Typography>
  );
};

const RunningTaskCard = ({ task, now, action, onOpen, onPause, onComplete }) => {
  const theme = useTheme();
  const timing = runningTaskTiming(task, now);
  const busy = Boolean(action);
  const fullTitle = Array.isArray(task.full_title) && task.full_title.length
    ? task.full_title.join(' > ')
    : task.title;

  const stopCardClick = (event, callback) => {
    event.stopPropagation();
    callback(task, timing);
  };

  return (
    <Box
      component="article"
      onClick={() => onOpen(task)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(task);
        }
      }}
      role="button"
      tabIndex={0}
      title={fullTitle}
      sx={{
        width: { xs: 190, sm: 285 },
        minWidth: { xs: 190, sm: 285 },
        minHeight: { xs: 44, sm: 52 },
        px: { xs: 1, sm: 1.5 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.6, sm: 1 },
        cursor: 'pointer',
        border: '4px solid',
        borderColor: timing.expired ? 'orange.main' : 'green.main',
        borderRadius: '18px',
        background: 'linear-gradient(90deg, #0A0F0D 60%, #1d2a25 100%)',
        boxShadow: timing.expired
          ? `0 4px 0 ${theme.palette.orange.main}, 0 0 14px ${alpha(theme.palette.orange.main, 0.55)}`
          : `0 4px 0 ${theme.palette.secondary.dark}, 0 0 9px ${alpha(theme.palette.secondary.main, 0.33)}`,
        transition: 'background 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 3,
        },
      }}
    >
      <CountdownRing {...timing} title={task.title} />
      <ScrollingTitle title={task.title} />
      <IconButton
        aria-label={`Pause ${task.title}`}
        disabled={busy}
        onClick={(event) => stopCardClick(event, onPause)}
        size="small"
        sx={{
          width: { xs: 33, sm: 38 },
          height: { xs: 30, sm: 34 },
          flexShrink: 0,
          color: 'primary.main',
          bgcolor: 'background.paper',
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: '50%',
          boxShadow: '0 4px 0 #007a8a, 0 0 10px rgba(0, 229, 255, 0.35)',
          '&:hover': { bgcolor: 'background.paper', transform: 'translate(-1px, -1px)' },
          '&:active': { transform: 'translateY(2px)', boxShadow: '0 1px 0 #007a8a' },
        }}
      >
        {action === 'pausing' ? <CircularProgress color="inherit" size={14} /> : <PauseIcon fontSize="small" />}
      </IconButton>
      <CompleteTaskButton
        compact
        id={`home-running-task-complete-${task.id}`}
        ariaLabel={`Complete ${task.title}`}
        disabled={busy}
        loading={action === 'completing'}
        onClick={(event) => stopCardClick(event, onComplete)}
      />
    </Box>
  );
};

const RunningTaskCards = ({ tasks, now, actions = {}, onOpen, onPause, onComplete }) => {
  if (!tasks?.length) return null;
  return (
    <Box component="section" sx={{ mb: 1 }}>
      <Typography variant="caption" color="orange.main" sx={{ display: 'block', mb: 0.5 }}>
        Running Tasks
      </Typography>
      <Stack
        id="home-running-tasks"
        direction="row"
        spacing={1.25}
        aria-label="Running tasks"
        sx={{
          overflowX: 'auto',
          px: 0.5,
          pt: 0.5,
          pb: 1,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {tasks.map((task) => (
          <RunningTaskCard
            key={task.id}
            task={task}
            now={now}
            action={actions[task.id]}
            onOpen={onOpen}
            onPause={onPause}
            onComplete={onComplete}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default RunningTaskCards;
